import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../ui';
import Icon from '../../../ui/Icon';
import SimpleButton from '../../../ui/molecules/Button';
import PillTag from '../../../ui/Pilltag';
import { useSettingsObject } from '../../settings/hooks';
import { getUserThemeColor, toRgba } from '../../../ui/theme/colors';

const DASHBOARD_DEFAULT_VIEW_KEY = 'dashboard_default_view';

export const getDefaultDashboardView = () => {
  try {
    return localStorage.getItem(DASHBOARD_DEFAULT_VIEW_KEY) || 'owner';
  } catch {
    return 'owner';
  }
};

export const setDefaultDashboardView = (viewId) => {
  try {
    localStorage.setItem(DASHBOARD_DEFAULT_VIEW_KEY, viewId);
  } catch {
    // localStorage not available
  }
};

const FloatingButton = styled.button`
  position: fixed;
  top: 10px;
  right: 16px;
  z-index: 9999;
  background: ${theme.colors.NEU_0};
  border: 2px solid ${(props) => props.$brandColor};
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px ${toRgba(theme.colors.NEU_999, 0.15)};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px ${toRgba(theme.colors.NEU_999, 0.2)};
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    top: 12px;
    right: 12px;
  }
`;

const MenuPanel = styled(Flex)`
  position: fixed;
  top: 16px;
  right: 68px;
  z-index: 9998;
  background: ${theme.colors.NEU_0};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 20px ${toRgba(theme.colors.NEU_999, 0.15)};
  min-width: 280px;
  border: 1px solid ${theme.colors.NEU_200};

  @media (max-width: 768px) {
    right: 60px;
    top: 12px;
    min-width: 240px;
  }
`;

const MenuItem = styled.div`
  width: 100%;
  padding: 12px 16px;
  background: ${(props) => (props.$active ? toRgba(props.$brandColor, 0.08) : 'transparent')};
  border: 1px solid ${(props) => (props.$active ? props.$brandColor : theme.colors.NEU_200)};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: ${(props) => toRgba(props.$brandColor, 0.08)};
    border-color: ${(props) => props.$brandColor};
  }

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${(props) => (props.$active ? props.$brandColor : theme.colors.NEU_100)};
  color: ${(props) => (props.$active ? theme.colors.NEU_0 : theme.colors.NEU_600)};
`;

/**
 * AdminDashboardSwitcher Component
 *
 * A floating button that allows admins to quickly switch between
 * Owner Dashboard V2 and Student Dashboard for demo/testing purposes.
 *
 * Only visible to users who have access to the Owner Dashboard.
 * Users can set a default view which persists in localStorage.
 */
const AdminDashboardSwitcher = ({ currentView, onViewChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [defaultView, setDefaultView] = useState(getDefaultDashboardView);
  const settings = useSettingsObject();
  const userTheme = settings?.theme;
  const brandColor = getUserThemeColor(userTheme?.color);

  const handleSetAsDefault = useCallback((viewId, e) => {
    e.stopPropagation();
    setDefaultDashboardView(viewId);
    setDefaultView(viewId);
  }, []);

  const views = [
    {
      id: 'owner',
      label: 'Owner Dashboard',
      icon: 'BarChart',
      description: 'Business goals & analytics',
    },
    {
      id: 'teacher',
      label: 'Teacher Dashboard',
      icon: 'Teacher',
      description: 'Student management & grading',
    },
    {
      id: 'student',
      label: 'Student Dashboard',
      icon: 'Student',
      description: 'Learning progress & recommendations',
    },
  ];

  const handleViewChange = (viewId) => {
    onViewChange(viewId);
    setIsOpen(false);
  };

  const currentViewData = views.find((v) => v.id === currentView) || views[0];
  const CurrentIcon = Icon[currentViewData.icon];

  return (
    <>
      <FloatingButton onClick={() => setIsOpen(!isOpen)} $brandColor={brandColor} title='Switch Dashboard View'>
        {CurrentIcon && <CurrentIcon size='small' color={brandColor} />}
      </FloatingButton>

      {isOpen && (
        <MenuPanel direction='column' align='flex-start'>
          <Flex direction='column' align='flex-start' width='100%' style={{ marginBottom: '12px' }}>
            <Typo.MediumText fontWeight='600' color={theme.colors.NEU_PRIMARY}>
              Dashboard View
            </Typo.MediumText>
            <Typo.Info color={theme.colors.NEU_600}>Switch between dashboards</Typo.Info>
          </Flex>

          {views.map((view) => {
            const ViewIcon = Icon[view.icon];
            const isActive = view.id === currentView;
            const isDefault = view.id === defaultView;

            return (
              <MenuItem key={view.id} $active={isActive} $brandColor={brandColor} onClick={() => handleViewChange(view.id)}>
                <IconWrapper $active={isActive} $brandColor={brandColor}>
                  {ViewIcon && <ViewIcon size='x-small' />}
                </IconWrapper>
                <Flex direction='column' align='flex-start' style={{ flex: 1 }}>
                  <Flex align='center' gap='8'>
                    <Typo.MediumText fontWeight='600' color={isActive ? brandColor : theme.colors.NEU_PRIMARY}>
                      {view.label}
                    </Typo.MediumText>
                    {isDefault && <PillTag label='Default' size='small' />}
                  </Flex>
                  <Typo.Info color={theme.colors.NEU_600}>{view.description}</Typo.Info>
                </Flex>
                {!isDefault && (
                  <SimpleButton variant='asText' size='small' onClick={(e) => handleSetAsDefault(view.id, e)}>
                    Set default
                  </SimpleButton>
                )}
              </MenuItem>
            );
          })}
        </MenuPanel>
      )}
    </>
  );
};

export default AdminDashboardSwitcher;
